
import { computed, defineComponent, ref, nextTick, onMounted, getCurrentInstance } from "vue-demi";
import axios from "@/plugins/http";
import { API_ROUTES } from "@/utils/API";
import { getServerFileErrorsFromUploadError } from "@/utils/documentUploadErrors";
import type { ServerFileErrorsByName } from "@/utils/documentUploadErrors";
import { TTask_Template } from "@/models/tasks";
import { SMDocumentsV2, SMTasks, SMUser } from "@/store";
import type { API_TFile } from "@/models/file";

export default defineComponent({
  name: "DocumentTaskTemplate",

  components: {
    ScrollContainer: () => import("@/modules/Documents/ScrollContainer.vue"),
    Uploader: () => import("@/modules/Documents/Uploader.vue"),
    PDFViewer: () => import("@/modules/Documents/PDFViewer.vue"),
    ParsedDocument: () => import("@/modules/Documents/ParsedDocument.vue"),
    DocumentTaskTemplateActions: () =>
      import("@/modules/Documents/DocumentTaskTemplateActions.vue"),
  },

  props: {
    variant: { type: String as () => "task" | "directory", default: "task" },
    taskTemplate: { type: Object as () => TTask_Template | null, default: () => null },
    directoryId: {
      type: Number,
      default: 0,
    },
    fileType: {
      type: String,
      default: "default",
    },
    isCase: {
      type: Boolean,
      default: false,
    }
  },

  setup(props) {
    const isCase = computed(() => props.taskTemplate?.product_type === "case" || props.isCase);

    const isParsedDocumentVisible = ref(false);

    const defaultText = computed(() => {
      const base = "Please upload PDF file(s) to"

      if (props.directoryId) return `${base} save the changes.`;

      return `${base} complete the task.`
    });

    const parsedText = computed(() => {
      const base = `Upload your document in PDF format. You can add only one file. After uploading, the data will be automatically extracted — please review it carefully, then click the ‘<b>Confirm & Save</b>’ button to`

      if (props.directoryId) return `${base} save the changes.`;

      return `${base} complete the task.`
    });

    const monthlyUploadText = computed(() => {
      return "Please upload PDF file and select the month and year this payslip applies to."
    });

    const dateProvidedText = computed(() => {
      return "Select the date when the employee’s permission to work in the UK expires"
    });

    const dateProvidedLastRTWCText = computed(() => {
      return "Please upload PDF file and select the date when you conducted the employee's right to work check."
    });

    const dateProvidedPreviousRTWCText = computed(() => {
      return "Please upload PDF file and select the date of the check."
    });

    const uploaderKey = ref(0);

    const uploaderRef = ref<{
      setServerFileErrors: (errors: ServerFileErrorsByName) => void;
    } | null>(null);

    function onFilenameValidationError(fileErrors: ServerFileErrorsByName) {
      uploaderRef.value?.setServerFileErrors(fileErrors);
    }

    function applyUploadFileErrors(error: unknown) {
      const fileErrors = getServerFileErrorsFromUploadError(error);

      if (!fileErrors) return false;

      onFilenameValidationError(fileErrors);

      return true;
    }

    // File Types
    const fileType = computed(() => props.taskTemplate?.document_template_v2?.file_type || props.fileType);

    const isParsedFileType = computed(() => {
      return ["cos", "ielts", "pte"].includes(fileType.value);
    });

    const isMonthlyUploadFileType = computed(() => {
      return ["monthly_upload"].includes(
        fileType.value
      );
    });

    const isDateProvidedFileType = computed(() => {
      return ["date_provided"].includes(
        fileType.value
      );
    });

    const isDateProvidedLastRTWCFileType = computed(() => {
      return ["date_provided_last_rtwc"].includes(
        fileType.value
      );
    });

    const isDateProvidedPreviousRTWCFileType = computed(() => {
      return ["date_provided_previous_rtwc"].includes(
        fileType.value
      );
    });

    // Files
    const files = ref<File[]>([]);

    function onFiles(payload: File[]) {
      files.value = payload;

      if (isParsedFileType.value) isParsedDocumentVisible.value = true;

      if (!payload.length) isParsedDocumentVisible.value = false;
    }

    const savedFiles = ref<API_TFile[]>([]);

    function onSavedFiles(payload: API_TFile[]) {
      savedFiles.value = payload;

      isParsedDocumentVisible.value = false;

      uploaderKey.value += 1;
    }

    // Example
    const pdfViewerKey = ref(0);

    const pdfViewerLink = ref("");

    const pdfViewerName = ref("");

    const isPdfViewerOpen = ref(false);

    function togglePdfViewer(value: boolean) {
      isPdfViewerOpen.value = value;
    }

    async function viewExample() {
      if (!props.taskTemplate?.document_template_v2.ID) return console.log("no documentTemplateId");

      try {
        const response = await axios.get<ArrayBuffer>(
          API_ROUTES.DOCUMENTS_EXAMPLE(props.taskTemplate.document_template_v2.ID),
          {
            responseType: "arraybuffer",
          }
        );

        const { data } = response;

        if (!data) return;

        const contentDisposition = response.headers["content-disposition"];

        pdfViewerLink.value = await SMDocumentsV2.createFileLink(data);

        pdfViewerName.value = await SMDocumentsV2.getFileNameContentDisposition(contentDisposition);

        pdfViewerKey.value += 1;

        await nextTick();

        togglePdfViewer(true);
      } catch (e) {
        console.log("failed to view example:", e);
      }
    }

    const parsedContentFormValid = ref(false);

    const isUploaderValid = ref(false);

    function onSetUploaderValid(valid: boolean) {
      isUploaderValid.value = valid;
    }

    const isScrolledBottom = ref(false)

    function onEndScrollRight() {
      if (files.value.length) isScrolledBottom.value = true;
    }

    const isSaveDisabled = computed(() => {
      const uploaderValid = isUploaderValid.value;

      const scrolledBottom = isScrolledBottom.value;
      const parsedContentValid = parsedContentFormValid.value;

      if (isParsedDocumentVisible.value) {
        return !(parsedContentValid && scrolledBottom && uploaderValid);
      }

      return !uploaderValid;
    })

    const parsedContent = ref(null);

    function setParsedContent({ form, valid }: { form: any, valid: boolean }) {
      isParsedDocumentVisible.value = true;

      parsedContent.value = form;

      parsedContentFormValid.value = valid;
    }

    const additionalData = ref(null);

    function setAdditionalData(payload) {
      additionalData.value = payload
    };

    const date = ref(null);

    function setDate(payload) {
      date.value = payload;
    }

    const startDate = ref(null);
    const endDate = ref(null);

    function setDates(payload) {
      startDate.value = payload?.startDate || "";
      endDate.value = payload?.endDate || "";
    }

    const parsingErrorMessage = ref("");
    const parsingErrorMessageTooltip = ref("");

    function setParsingError({ errorMessage, errorMessageTooltip }: { errorMessage: string, errorMessageTooltip: string }) {
      isParsedDocumentVisible.value = false;

      parsingErrorMessage.value = errorMessage;

      parsingErrorMessageTooltip.value = errorMessageTooltip;
    }

    const loading = ref(false);

    onMounted(async () => {
      const directoryId = props.taskTemplate?.directory_id || 0;

      if (!directoryId) return; // note: directory_id exists only for completed tasks

      try {
        loading.value = true;

        const { data } = await axios.get(API_ROUTES.DOCUMENTS_DIRECTORY_FILES(directoryId));

        if (!data) return;

        savedFiles.value = data;

        isParsedDocumentVisible.value = false;
      } finally {
        loading.value = false;
      }
    });

    // Upload Restrictions
    const canUpload = computed(() => {
      if (!isCase.value && SMUser.isComplianceBlocked) return false;

      if (props.directoryId) return true;

      if (SMUser.isEmployee && SMUser.user.role !== SMTasks.user.role) return false;

      return true;
    })

    const router = getCurrentInstance()?.proxy?.$router;
    const route = getCurrentInstance()?.proxy?.$route;

    async function goToDocuments(full_route: string) {
      if (!route.path) return;

      let url = route.path.replace(/\/upload\b.*$/, '');

      if (full_route) url += `?path=${full_route}`;

      await router.push(url);
    }

    async function saveDocumentsToDirectory(files: File[]) {
      if (!props.directoryId) {
        console.log("No directory ID");
        return;
      }

      try {
        loading.value = true;

        const formData = new FormData();

        files.forEach((file) => formData.append("file", file));

        if (additionalData.value)
          formData.append("additional_data", JSON.stringify(additionalData.value));

        if (parsedContent.value) {
          formData.append("document_data", JSON.stringify(parsedContent.value));
        } else if (date.value) {
          formData.append("document_data", JSON.stringify({ date: date.value }));
        } else if (startDate.value && endDate.value) {
          formData.append("document_data", JSON.stringify({ start_date: startDate.value, end_date: endDate.value }));
        }

        const { data } = await axios.post(API_ROUTES.DOCUMENTS_UPLOAD_TO_DIRECTORY(props.directoryId), formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });

        if (!data?.full_route) return;

        goToDocuments(data.full_route);

        // console.log(Object.fromEntries(formData.entries()));
      } catch (error) {
        if (!applyUploadFileErrors(error)) {
          console.log("Upload failed", error);
        }
      } finally {
        loading.value = false;
      }
    }

    return {
      uploaderRef,
      onFilenameValidationError,
      files,
      onFiles,
      savedFiles,
      onSavedFiles,
      fileType,
      isParsedFileType,
      isMonthlyUploadFileType,
      isDateProvidedFileType,
      isDateProvidedLastRTWCFileType,
      isDateProvidedPreviousRTWCFileType,
      isCase,
      isParsedDocumentVisible,
      pdfViewerKey,
      pdfViewerLink,
      pdfViewerName,
      isPdfViewerOpen,
      togglePdfViewer,
      viewExample,
      isSaveDisabled,
      onEndScrollRight,
      parsedContent,
      setParsedContent,
      setParsingError,
      additionalData,
      setAdditionalData,
      date,
      setDate,
      startDate,
      endDate,
      setDates,
      parsingErrorMessage,
      parsingErrorMessageTooltip,
      parsedContentFormValid,
      isScrolledBottom,
      onSetUploaderValid,
      canUpload,
      saveDocumentsToDirectory,
      defaultText,
      parsedText,
      monthlyUploadText,
      dateProvidedText,
      dateProvidedLastRTWCText,
      dateProvidedPreviousRTWCText,
      uploaderKey,
    };
  },
});
